import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { fetchJSON } from 'api/fetch';

import { SLICE_NAME } from './constants';
import {
  CompanyHours,
  CompanyHoursParams,
  CompanyHoursPayload,
  CompanyHoursPayloadItem,
  CompanyHoursState,
  ShiftPayload,
} from './types';

export const fetchCompanyHours = createAsyncThunk<
  CompanyHoursPayload,
  CompanyHoursParams
>(`${SLICE_NAME}/fetch`, async (params: CompanyHoursParams) => {
  const resp = await fetchJSON(
    // eslint-disable-next-line
    `/schedule_builder/labor/company_hours?start_date=${params.startDate}&end_date=${params.endDate}`
  );
  return resp;
});

const companyHoursAdapter = createEntityAdapter<CompanyHours>();

export const initialState: CompanyHoursState =
  companyHoursAdapter.getInitialState({
    status: 'idle',
    error: undefined,
  });

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchCompanyHours.pending, state => {
      state.status = 'pending';
    });
    builder.addCase(fetchCompanyHours.fulfilled, (state, action) => {
      const data = action.payload?.data ?? [];
      const items = data.map((item: CompanyHoursPayloadItem) => ({
        id: item.job_id.toString(),
        type: 'companyHours' as const,
        jobId: item.job_id,
        shifts: item.shifts.map((shift: ShiftPayload) => ({
          id: shift.id,
          hours: shift.hours,
          startAt: shift.start_at,
          endAt: shift.end_at,
        })),
      }));
      companyHoursAdapter.setAll(state, items);
      state.status = 'succeeded';
    });
    builder.addCase(fetchCompanyHours.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch company hours';
    });
  },
});

export const reducer = slice.reducer;

export const {
  selectById: selectCompanyHoursById,
  selectIds: selectCompanyHoursIds,
  selectEntities: selectCompanyHoursEntities,
  selectAll: selectAllCompanyHours,
  selectTotal: selectTotalCompanyHours,
} = companyHoursAdapter.getSelectors();
